// Copyright (c) Ping. All rights reserved.
//
// Message-tile renderer for Slack `mrkdwn`. Parses a message body with
// `parseMrkdwn` (the receiver complement of the composer's serializer)
// and renders bold / italic / strike / inline-code / links / @mentions
// plus the block constructs — blockquotes, fenced code blocks, and
// nested bulleted / numbered lists — that the composer can produce.
//
// Layout contract: for a SIMPLE message (one single-line paragraph) the
// renderer reproduces the legacy bubble layout exactly — inline segment
// `<Text>` siblings inside a `flex-row flex-wrap` row, so the caller's
// trailing meta (timestamp + read tick) flows on the same wrapped line
// (the iMessage-style "Hi 7:00 PM ✓"). For anything richer (multi-line,
// lists, quotes, code) the blocks stack in a column and the trailing
// meta drops onto its own final row — matching how the existing
// `RichTextBody` path renders meta after a block body.
//
// Pure presentational + theme colors supplied by the caller, so it works
// on every bubble variant (own/other, bubble/compact) and both platforms.

import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { Check, ChevronDown, ChevronUp, Copy } from 'lucide-react-native';
import { languageLabel } from '@ping/enriched-text-input';
import {
  parseMrkdwn,
  type KnownMention,
  type MrkdwnBlock,
  type MrkdwnInline,
  type MrkdwnListItem,
} from './mrkdwn/parseMrkdwn';

const MONO_FONT_FAMILY =
  'Menlo, Monaco, Consolas, "Liberation Mono", monospace';

/** Theme-resolved colors the renderer needs. Supplied by the bubble so
 *  own/other variants stay correct. */
export interface MrkdwnPalette {
  /** Tappable link color (+ underline). */
  readonly link: string;
  /** Mention pill foreground. */
  readonly mention: string;
  /** Mention pill background (usually a low-alpha wash of `mention`). */
  readonly mentionBackground: string;
  /** Inline-code / code-block text color. */
  readonly code: string;
  /** Inline-code / code-block surface. */
  readonly codeBackground: string;
  /** Left rule color of a blockquote. */
  readonly quoteBar: string;
}

export interface MrkdwnTextProps {
  readonly body: string;
  /** Flat mention id/handle list from the message (for @handle resolution). */
  readonly mentions?: ReadonlyArray<string> | undefined;
  /**
   * Known mentions (id + canonical display name) decoded from the
   * message envelope's ranged `payload.mentions`. Lets the renderer
   * match multi-word display names (`@aik 11`) as a single pill.
   */
  readonly knownMentions?: ReadonlyArray<KnownMention> | undefined;
  /** Base text style (font size / line height / body color). */
  readonly textStyle?: TextStyle | undefined;
  readonly palette: MrkdwnPalette;
  readonly onMentionPress?: ((userId: string) => void) | undefined;
  /**
   * Opens a tapped link. The host owns this (it already imports
   * `Linking`) so the renderer stays purely presentational. When
   * omitted, links render styled but inert.
   */
  readonly onLinkPress?: ((url: string) => void) | undefined;
  /**
   * Inline trailing nodes (timestamp + read tick + pin glyph). Appended
   * as flex siblings on a simple message's wrapped line, or on a final
   * trailing row for rich/multi-line messages.
   */
  readonly trailingMeta?: React.ReactNode;
  readonly testID?: string | undefined;
}

interface InlineOpts {
  readonly base: TextStyle | undefined;
  readonly palette: MrkdwnPalette;
  readonly onMentionPress?: ((userId: string) => void) | undefined;
  readonly onLinkPress?: ((url: string) => void) | undefined;
}

/** Resolve a text/link segment's emphasis flags into an RN style. */
function markStyle(
  seg: Extract<MrkdwnInline, { type: 'text' | 'link' }>,
  opts: InlineOpts,
): TextStyle {
  const s: TextStyle = {};
  if (seg.bold) s.fontWeight = '700';
  if (seg.italic) s.fontStyle = 'italic';
  if (seg.strike) s.textDecorationLine = 'line-through';
  if (seg.type === 'text' && seg.code) {
    s.fontFamily = MONO_FONT_FAMILY;
    s.color = opts.palette.code;
    s.backgroundColor = opts.palette.codeBackground;
  }
  return s;
}

/** Render one inline node as a `<Text>` (valid both as a flex sibling
 *  and nested inside a parent `<Text>`). */
function renderInline(
  inline: MrkdwnInline,
  key: React.Key,
  opts: InlineOpts,
): React.ReactNode {
  // `opts.base` is merged into every leaf so the inline segments carry
  // the body font size / line-height / color even on the "simple" path
  // where they render as flex siblings with no parent `<Text>` to
  // inherit from. On the block path the parent `<Text>` already applies
  // it; re-applying here is a harmless no-op.
  if (inline.type === 'mention') {
    return (
      <Text
        key={key}
        accessibilityRole="link"
        onPress={
          opts.onMentionPress !== undefined
            ? () => opts.onMentionPress?.(inline.userId)
            : undefined
        }
        style={[
          opts.base,
          styles.mention,
          { color: opts.palette.mention, backgroundColor: opts.palette.mentionBackground },
        ]}
      >
        @{inline.handle}
      </Text>
    );
  }
  if (inline.type === 'link') {
    return (
      <Text
        key={key}
        // Allow a long URL token to wrap inside the bubble instead of
        // overflowing the rounded rect.
        style={[
          opts.base,
          markStyle(inline, opts),
          styles.link,
          { color: opts.palette.link },
        ]}
        onPress={
          opts.onLinkPress !== undefined
            ? () => opts.onLinkPress?.(inline.url)
            : undefined
        }
      >
        {inline.text}
      </Text>
    );
  }
  return (
    <Text key={key} style={[opts.base, markStyle(inline, opts)]}>
      {inline.text}
    </Text>
  );
}

/** Render a run of inline nodes as children of a parent `<Text>`. */
function renderInlineRun(
  inlines: ReadonlyArray<MrkdwnInline>,
  opts: InlineOpts,
  keyPrefix: string,
): React.ReactNode[] {
  return inlines.map((inl, i) => renderInline(inl, `${keyPrefix}-${i}`, opts));
}

/** Join multiple inline lines into one parent-`<Text>` child list, with
 *  newline separators between lines. */
function renderLines(
  lines: ReadonlyArray<ReadonlyArray<MrkdwnInline>>,
  opts: InlineOpts,
  keyPrefix: string,
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  lines.forEach((line, li) => {
    if (li > 0) out.push(<Text key={`${keyPrefix}-br-${li}`}>{'\n'}</Text>);
    out.push(...renderInlineRun(line, opts, `${keyPrefix}-l${li}`));
  });
  return out;
}

function ListBlock({
  block,
  opts,
  depth,
  keyPrefix,
  textStyle,
}: {
  block: Extract<MrkdwnBlock, { type: 'list' }>;
  opts: InlineOpts;
  depth: number;
  keyPrefix: string;
  textStyle: TextStyle | undefined;
}): React.ReactElement {
  return (
    <View style={styles.list}>
      {block.items.map((item: MrkdwnListItem, idx) => {
        const marker = block.ordered ? `${idx + 1}.` : '•';
        return (
          <View key={`${keyPrefix}-i${idx}`} style={styles.listItem}>
            <View style={styles.listRow}>
              <Text style={[textStyle, styles.listMarker]}>{marker}</Text>
              <Text style={[textStyle, styles.listContent]}>
                {renderInlineRun(item.inlines, opts, `${keyPrefix}-i${idx}`)}
              </Text>
            </View>
            {item.children.map((child, ci) =>
              child.type === 'list' ? (
                <ListBlock
                  key={`${keyPrefix}-i${idx}-c${ci}`}
                  block={child}
                  opts={opts}
                  depth={depth + 1}
                  keyPrefix={`${keyPrefix}-i${idx}-c${ci}`}
                  textStyle={textStyle}
                />
              ) : null,
            )}
          </View>
        );
      })}
    </View>
  );
}

function renderBlock(
  block: MrkdwnBlock,
  index: number,
  opts: InlineOpts,
  textStyle: TextStyle | undefined,
): React.ReactNode {
  const key = `b${index}`;
  switch (block.type) {
    case 'paragraph':
      return (
        <Text key={key} style={textStyle}>
          {renderLines(block.lines, opts, key)}
        </Text>
      );
    case 'quote':
      return (
        <View key={key} style={styles.quote}>
          <View style={[styles.quoteBar, { backgroundColor: opts.palette.quoteBar }]} />
          <Text style={[textStyle, styles.quoteText]}>
            {renderLines(block.lines, opts, key)}
          </Text>
        </View>
      );
    case 'code':
      return <CodeBlockTile key={key} block={block} palette={opts.palette} />;
    case 'list':
      return (
        <ListBlock
          key={key}
          block={block}
          opts={opts}
          depth={0}
          keyPrefix={key}
          textStyle={textStyle}
        />
      );
  }
}

// A long code block is collapsed by default; these bound the preview.
const MAX_COLLAPSED_LINES = 12;
const MAX_COLLAPSED_CHARS = 700;

/** Copy text to the clipboard (web). Returns true when it ran. */
function copyToClipboard(text: string): boolean {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard !== undefined) {
      void navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* clipboard unavailable — no-op */
  }
  return false;
}

/**
 * Code-block tile with a language header, a Copy affordance, and a
 * length-gated "View full snippet" collapse (collapsed by default for
 * long blocks). Stateful, so it lives as its own component.
 */
const CodeBlockTile: React.FC<{
  readonly block: Extract<MrkdwnBlock, { type: 'code' }>;
  readonly palette: MrkdwnPalette;
}> = ({ block, palette }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const lines = block.text.split('\n');
  const isLong =
    lines.length > MAX_COLLAPSED_LINES || block.text.length > MAX_COLLAPSED_CHARS;
  const shown =
    expanded || !isLong ? block.text : lines.slice(0, MAX_COLLAPSED_LINES).join('\n');
  const hiddenLines = Math.max(0, lines.length - MAX_COLLAPSED_LINES);

  const onCopy = (): void => {
    if (copyToClipboard(block.text)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const langText =
    block.language !== undefined && block.language.length > 0
      ? languageLabel(block.language)
      : 'Code';

  return (
    <View style={[styles.codeBlock, { backgroundColor: palette.codeBackground }]}>
      <View style={styles.codeHeader}>
        <Text
          numberOfLines={1}
          style={[styles.codeBlockLang, { color: palette.code }]}
        >
          {langText}
        </Text>
        <Pressable
          onPress={onCopy}
          accessibilityRole="button"
          accessibilityLabel={copied ? 'Copied' : 'Copy code'}
          hitSlop={8}
          style={styles.codeAction}
        >
          {copied ? (
            <Check size={13} strokeWidth={2} color={palette.code} />
          ) : (
            <Copy size={13} strokeWidth={2} color={palette.code} />
          )}
          <Text style={[styles.codeActionLabel, { color: palette.code }]}>
            {copied ? 'Copied' : 'Copy'}
          </Text>
        </Pressable>
      </View>
      <Text style={[styles.codeBlockText, { color: palette.code }]}>{shown}</Text>
      {isLong ? (
        <Pressable
          onPress={() => setExpanded((e) => !e)}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          hitSlop={6}
          style={styles.codeExpand}
        >
          {expanded ? (
            <ChevronUp size={14} strokeWidth={2} color={palette.code} />
          ) : (
            <ChevronDown size={14} strokeWidth={2} color={palette.code} />
          )}
          <Text style={[styles.codeExpandLabel, { color: palette.code }]}>
            {expanded
              ? 'Show less'
              : `View full snippet${hiddenLines > 0 ? ` (${hiddenLines} more lines)` : ''}`}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
};

export const MrkdwnText = React.memo<MrkdwnTextProps>(function MrkdwnText({
  body,
  mentions,
  knownMentions,
  textStyle,
  palette,
  onMentionPress,
  onLinkPress,
  trailingMeta,
  testID,
}) {
  const blocks = useMemo(
    () => parseMrkdwn(body, mentions ?? [], knownMentions ?? []),
    [body, mentions, knownMentions],
  );
  const opts = useMemo<InlineOpts>(
    () => ({
      base: textStyle,
      palette,
      ...(onMentionPress !== undefined ? { onMentionPress } : {}),
      ...(onLinkPress !== undefined ? { onLinkPress } : {}),
    }),
    [textStyle, palette, onMentionPress, onLinkPress],
  );

  // Simple message — one single-line paragraph. Reproduce the legacy
  // inline layout so the trailing meta flows on the wrapped line.
  const simple =
    blocks.length === 1 &&
    blocks[0]!.type === 'paragraph' &&
    (blocks[0] as Extract<MrkdwnBlock, { type: 'paragraph' }>).lines.length <= 1;

  if (simple) {
    const line =
      (blocks[0] as Extract<MrkdwnBlock, { type: 'paragraph' }>).lines[0] ?? [];
    return (
      <View style={styles.inlineRow} testID={testID}>
        {line.map((inl, i) => renderInline(inl, `s-${i}`, opts))}
        {trailingMeta}
      </View>
    );
  }

  return (
    <View testID={testID} style={styles.column}>
      {blocks.map((b, i) => renderBlock(b, i, opts, textStyle))}
      {trailingMeta !== undefined && trailingMeta !== null ? (
        <View style={styles.inlineRow}>{trailingMeta}</View>
      ) : null}
    </View>
  );
});

MrkdwnText.displayName = 'MrkdwnText';

const styles = StyleSheet.create({
  inlineRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  column: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 4,
  },
  link: {
    textDecorationLine: 'underline',
  },
  mention: {
    fontWeight: '600',
    paddingHorizontal: 2,
    borderRadius: 4,
  },
  quote: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  quoteBar: {
    width: 3,
    borderRadius: 2,
    marginRight: 8,
  },
  quoteText: {
    flex: 1,
    opacity: 0.9,
  },
  codeBlock: {
    marginVertical: 2,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
  },
  codeBlockText: {
    fontFamily: MONO_FONT_FAMILY,
    fontSize: 13,
    lineHeight: 18,
  },
  codeBlockLang: {
    fontFamily: MONO_FONT_FAMILY,
    fontSize: 10,
    opacity: 0.6,
  },
  codeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
    gap: 8,
  },
  codeAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    opacity: 0.75,
  },
  codeActionLabel: {
    fontSize: 11,
    fontWeight: '600',
  },
  codeExpand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 8,
    opacity: 0.85,
  },
  codeExpandLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  list: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  listItem: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  listRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingLeft: 8,
  },
  listMarker: {
    minWidth: 18,
    marginRight: 4,
  },
  listContent: {
    flex: 1,
  },
});
